import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { ExternalLink, X, FileText, ListTodo } from 'lucide-react';

interface ProjectDetailsModalProps {
  projectId: string | null;
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'tasks' | 'brief';
}

export function ProjectDetailsModal({ projectId, isOpen, onClose, initialTab = 'tasks' }: ProjectDetailsModalProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'tasks' | 'brief'>(initialTab);

  if (!projectId) return null;

  const handleOpenInNewTab = () => {
    window.open(`/projects/${projectId}?tab=${activeTab}`, '_blank');
  };

  const handleNavigateToProject = () => {
    navigate(`/projects/${projectId}?tab=${activeTab}`);
    onClose();
  };

  // Build iframe URL with tab parameter
  const iframeSrc = `/projects/${projectId}?tab=${activeTab}`;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Dettagli Progetto"
      size="fullscreen"
      mobileHeight="full"
      noPadding
    >
      <div className="h-full flex flex-col bg-[#FBFBFA] dark:bg-gray-900">
        {/* Action bar */}
        <div className="flex items-center justify-between p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-4">
            {/* Tab buttons */}
            <div className="flex items-center gap-1 bg-[rgba(55,53,47,0.04)] dark:bg-[rgba(255,255,255,0.04)] rounded-lg p-1">
              <button
                onClick={() => setActiveTab('tasks')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'tasks'
                    ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm'
                    : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                }`}
              >
                <ListTodo className="h-3.5 w-3.5" />
                Task
              </button>
              <button
                onClick={() => setActiveTab('brief')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'brief'
                    ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm'
                    : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                Brief
              </button>
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-[rgba(55,53,47,0.09)] dark:bg-gray-700" />

            {/* Navigation buttons */}
            <button
              onClick={handleNavigateToProject}
              className="notion-ghost-btn"
            >
              Vai alla pagina progetto
            </button>
            <button
              onClick={handleOpenInNewTab}
              className="notion-ghost-btn"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Apri in nuova scheda
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="notion-ghost-btn p-1.5 hover:bg-[rgba(55,53,47,0.08)] rounded transition-colors"
            title="Chiudi"
          >
            <X className="h-4 w-4 text-[#787774]" />
          </button>
        </div>

        {/* iFrame container */}
        <div className="flex-1 relative">
          <iframe
            key={activeTab} // Force reload when tab changes
            src={iframeSrc}
            className="w-full h-full border-0"
            title="Project Details"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
